import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import {
    BehaviorSubject,
    EMPTY,
    Observable,
    ReplaySubject,
    distinctUntilChanged,
    filter,
    firstValueFrom,
    from,
    map,
    of,
    startWith,
    switchMap,
    tap,
} from 'rxjs';
import { take, timeout } from 'rxjs/operators';
import { SecureLocalStorageService } from 'shared';

import { OidcConfig, UserRegistrationModel, UserRegistrationResponseModel } from '../../models/';
import { AuthUser } from '../../models/auth-user.model';
import { UserRepositoryService } from '../../repositories';
import { AuthSettingsService } from './auth-settings.service';

export const TENANT_KEY = 'ACTIVE_TENANT_ID';
export const IDP_HINT_PARAM = 'kc_idp_hint';
export const IDP_HINT_KEY = 'IDP_HINT';
export const LOGOUT_TIMEOUT_MS = 250; // .25 seconds
export const TOKEN_EXPIRY_OFFSET_MS = 10000; // 10 seconds
export const TOKEN_REFRESH_TIMEOUT_MS = 10000; // 10 seconds

type RefreshStatus =
    | { status: 'IDLE' }
    | { status: 'TOKEN_STILL_VALID'; accessToken: string }
    | { status: 'REFRESHING'; refreshToken: string }
    | { status: 'SUCCESS'; accessToken: string }
    | { status: 'FAILED'; error: unknown };

@Injectable({
    providedIn: 'root',
})
export class OidcAuthService {
    private isInitialized = false;
    public onInitialize: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

    private readonly isBrowser: boolean = isPlatformBrowser(inject(PLATFORM_ID));
    private readonly authSettingsService = inject(AuthSettingsService);
    private readonly oauthService = inject(OAuthService);
    private readonly localStorageService = inject(SecureLocalStorageService);

    private readonly userRepository = inject(UserRepositoryService);

    public readonly user: BehaviorSubject<AuthUser | null | undefined> = new BehaviorSubject<
        AuthUser | null | undefined
    >(undefined);

    public loggedIn: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private oidcConfig: OidcConfig;

    private readonly refreshStatus = new BehaviorSubject<RefreshStatus>({ status: 'IDLE' });

    public init(): Observable<void> {
        this.persistIdpHintFromUrl();

        if (!this.isInitialized) {
            return this.authSettingsService.authSettings.pipe(
                filter(Boolean),
                take(1),
                tap((oidcConfig) => {
                    console.debug(`Initializing Authentication for realm: '${oidcConfig.keycloakRealm}'`);

                    this.oidcConfig = oidcConfig;

                    this.oauthService.setStorage(this.localStorageService);
                    this.oauthService.configure(authCodeFlowConfig(oidcConfig));
                    this.oauthService.setupAutomaticSilentRefresh({
                        timeoutFactor: 0.6,
                    });
                }),
                switchMap(() => from(this.oauthService.loadDiscoveryDocument())),
                tap((x) => {
                    console.debug(`Discovery document loaded: ${x}`);
                    this.isInitialized = true;
                }),
                map((): void => void 0)
            );
        }
        console.warn(`Cannot initialize Auth as it's already initialized`);

        return EMPTY;
    }

    public checkAuthenticatedOrLogin(): Observable<boolean> {
        return this.init().pipe(
            switchMap(() => this.validateLogin()),
            map(() => {
                console.debug('Checking if user is authenticated');
                if (!this.isAuthenticated()) {
                    console.debug('User is not authenticated');
                    if (this.oidcConfig.allowAnonymous?.includes(window.location.pathname as `/${string}`) ?? false) {
                        console.debug('User is not authenticated, but the page is allowed for anonymous access');
                        this.user.next(null);
                        return true;
                    }

                    if (this.isBrowser) {
                        console.debug('User is not authenticated, trying to redirect to login');
                        this.login();
                    } else {
                        console.debug('User is not authenticated, but page is server rendered');
                    }
                    this.user.next(null);
                    return false;
                } else {
                    console.debug('User is authenticated');
                    const claims = this.oauthService.getIdentityClaims();

                    const fullName =
                        claims['name'] ||
                        `${claims['given_name']} ${claims['family_name']}`.trim() ||
                        claims['preferred_username'];

                    this.user.next({
                        id: claims['MtpUserId'],
                        fullName,
                        username: claims['onego']['user_name'],
                        timestamp: new Date(claims['onego']['created_at']),
                    });
                    return true;
                }
            }),
            tap((success) => {
                this.oauthService.events
                    .pipe(
                        map(() => this.oauthService.hasValidAccessToken()),
                        startWith(this.oauthService.hasValidAccessToken()),
                        distinctUntilChanged(),
                        tap((loggedIn) => {
                            this.loggedIn.next(loggedIn);
                        })
                    )
                    .subscribe();
                this.onInitialize.next(success);
            })
        );
    }

    public getClaims(): object {
        return this.oauthService.getIdentityClaims();
    }

    public login(redirectUrl?: string): void {
        this.validateInit();
        this.oauthService.initLoginFlow(redirectUrl, this.getAuthorizationParams());
    }

    public logout(): void {
        this.validateInit();
        this.localStorageService.removeItem(IDP_HINT_KEY);
        this.oauthService.logOut();

        // Clear user information after logout, in case logging out doesn't redirect to the login page.
        // Normally the OIDC service will redirect to the login page, but in case that is somehow not happening,
        // we want to ensure that the user is logged out from the application.
        setTimeout(() => {
            this.user.next(null);
        }, LOGOUT_TIMEOUT_MS);
    }

    public isAuthenticated(): boolean {
        this.validateInit();
        return this.oauthService.hasValidAccessToken();
    }

    public getToken(): string {
        return this.localStorageService.getItem('access_token');
    }

    public registerUser(user: UserRegistrationModel): Observable<UserRegistrationResponseModel> {
        return this.userRepository.registerUser(user);
    }

    public async refreshTokenIfExpired(): Promise<RefreshStatus> {
        // If we're already refreshing, wait for the result to avoid concurrent refreshes
        if (this.refreshStatus.value.status === 'REFRESHING') {
            return firstValueFrom(this.refreshStatus.pipe(filter((status) => status.status !== 'REFRESHING')));
        }

        // If our access token is still valid, return the current token.
        // (We'll still count it as expired if it's set to expire within the next period as defined by TOKEN_EXPIRY_OFFSET_MS)
        if (
            this.oauthService.hasValidAccessToken() &&
            Date.now() < this.oauthService.getAccessTokenExpiration() - TOKEN_EXPIRY_OFFSET_MS
        ) {
            return { status: 'TOKEN_STILL_VALID', accessToken: this.oauthService.getAccessToken() };
        }

        return this.refreshToken();
    }

    public async refreshToken(): Promise<RefreshStatus> {
        // Check if we have a refresh token
        const refreshToken = this.oauthService.getRefreshToken();
        if (!refreshToken) {
            const status: RefreshStatus = { status: 'FAILED', error: 'No refresh token available' };
            this.refreshStatus.next(status);
            return status;
        }

        // Attempt refreshing the token
        try {
            console.debug('[OidcAuthService] Attempting to refresh token');
            this.refreshStatus.next({ status: 'REFRESHING', refreshToken });
            await firstValueFrom(from(this.oauthService.refreshToken()).pipe(timeout(TOKEN_REFRESH_TIMEOUT_MS)));
            const status: RefreshStatus = this.oauthService.hasValidAccessToken()
                ? { status: 'SUCCESS', accessToken: this.oauthService.getAccessToken() }
                : { status: 'FAILED', error: 'Token refresh did not result in a valid access token' };
            console.debug('[OidcAuthService] Token refresh result:', status);
            this.refreshStatus.next(status);
            return status;
        } catch (error) {
            const status: RefreshStatus = { status: 'FAILED', error };
            console.debug('[OidcAuthService] Token refresh failed:', status);
            this.refreshStatus.next(status);
            return status;
        }
    }

    /**
     * Extra parameters to send along with the authorization request. `kc_idp_hint` makes Keycloak
     * skip its own login page and redirect straight to the named identity provider, so a user
     * arriving from an external portal never sees the email prompt.
     */
    private getAuthorizationParams(): Record<string, string> {
        const idpHint = this.localStorageService.getItem<string>(IDP_HINT_KEY);

        return idpHint ? { [IDP_HINT_PARAM]: idpHint } : {};
    }

    /**
     * Stores the `kc_idp_hint` query parameter on first sight, so later logins (e.g. the auth guard
     * redirecting after session expiry) keep skipping the Keycloak email prompt. Cleared on logout.
     */
    private persistIdpHintFromUrl(): void {
        if (!this.isBrowser) {
            return;
        }

        const idpHint = new URLSearchParams(window.location.search).get(IDP_HINT_PARAM);
        if (idpHint) {
            this.localStorageService.setItem(IDP_HINT_KEY, idpHint);
        }
    }

    private validateLogin(): Observable<boolean> {
        this.validateInit();

        if (this.isBrowser) {
            return from(this.oauthService.tryLogin());
        }
        this.user.next(null);
        return of(false);
    }

    private validateInit(): void {
        if (!this.isInitialized) {
            throw new Error('AuthService not initialized');
        }
    }
}

export function authCodeFlowConfig(config: OidcConfig): AuthConfig {
    return {
        // Url of the Identity Provider
        issuer: `${config.keycloakUrl}/realms/${config.keycloakRealm}`,

        // URL of the SPA to redirect the user to after login
        redirectUri: config.redirectUri,

        // The SPA's id. The SPA is registered with this id at the auth-server
        // clientId: 'server.code',
        clientId: config.keycloakClientId,

        // set the scope for the permissions the client should request
        // The first four are defined by OIDC.
        // Important: Request offline_access to get a refresh token
        // The api scope is a usecase specific one
        scope: config.scope,

        // Just needed if your auth server demands a secret. In general, this
        // is a sign that the auth server is not configured with SPAs in mind
        // and it might not enforce further best practices vital for security
        // such applications.
        // dummyClientSecret: 'secret',
        responseType: 'code',

        showDebugInformation: config.debug || false,
    };
}
